import type { UnitCode } from '~/graphql/graphql';

// Exact conversion: 1 inch³ = 0.000016387064 m³
const CUBIC_INCH_TO_CUBIC_METER = 0.000016387064;

/**
 * Units of the given UOM contained in one piece of lumber with the given dimensions.
 * Mirrors UnitUtil.unitsPerPieceRaw in the backend.
 */
export function unitsPerPiece(
  unitCode: UnitCode,
  lengthInches: number,
  widthInches: number,
  thicknessInches: number
): number {
  switch (unitCode) {
    case 'LF':
      if (lengthInches <= 0)
        throw new Error(`Invalid lengthInches for LF: ${lengthInches}`);
      return lengthInches / 12;
    case 'MSF':
      if (lengthInches <= 0 || widthInches <= 0)
        throw new Error(
          `Invalid dimensions for MSF: length=${lengthInches}, width=${widthInches}`
        );
      return (lengthInches * widthInches) / 144_000;
    case 'BF':
      if (lengthInches <= 0 || widthInches <= 0 || thicknessInches <= 0)
        throw new Error(
          `Invalid dimensions for BF: length=${lengthInches}, width=${widthInches}, thickness=${thicknessInches}`
        );
      return (lengthInches * widthInches * thicknessInches) / 144;
    case 'MBF':
      if (lengthInches <= 0 || widthInches <= 0 || thicknessInches <= 0)
        throw new Error(
          `Invalid dimensions for MBF: length=${lengthInches}, width=${widthInches}, thickness=${thicknessInches}`
        );
      return (lengthInches * widthInches * thicknessInches) / 144_000;
    case 'CBM':
      if (lengthInches <= 0 || widthInches <= 0 || thicknessInches <= 0)
        throw new Error(
          `Invalid dimensions for CBM: length=${lengthInches}, width=${widthInches}, thickness=${thicknessInches}`
        );
      return (
        lengthInches * widthInches * thicknessInches * CUBIC_INCH_TO_CUBIC_METER
      );
    case 'PCS':
      return 1;
    default:
      throw new Error(`Unsupported UnitCode: ${unitCode satisfies never}`);
  }
}

/**
 * Converts a quantity in pieces (PCS) to the given unit.
 * Mirrors the PCS → toUom path of UnitUtil.convertUnitsRaw in the backend.
 *
 * Result is rounded to 3 decimal places, matching backend SCALE_DEFAULT.
 */
export function convertPiecesToUnit(
  pieces: number,
  unitCode: UnitCode,
  lengthInches: number,
  widthInches: number,
  thicknessInches: number
): number {
  if (pieces === 0) return 0;
  const perPiece = unitsPerPiece(
    unitCode,
    lengthInches,
    widthInches,
    thicknessInches
  );
  return Math.round(pieces * perPiece * 1000) / 1000;
}

export function toStandardUnits(
  quantity: number,
  fromUnit: UnitCode,
  toUnit: UnitCode,
  lengthInches: number,
  widthInches: number,
  thicknessInches: number
): number {
  if (fromUnit === toUnit) return quantity;
  const pieces =
    quantity /
    unitsPerPiece(fromUnit, lengthInches, widthInches, thicknessInches);
  return convertPiecesToUnit(
    pieces,
    toUnit,
    lengthInches,
    widthInches,
    thicknessInches
  );
}

/**
 * Returns the stock quantity converted from pieces to the product's configured unit.
 */
export function getStockForUnit(
  stock: number,
  unitCode: UnitCode,
  lengthInches: number,
  widthInches: number,
  thicknessInches: number
): number {
  return convertPiecesToUnit(
    stock,
    unitCode,
    lengthInches,
    widthInches,
    thicknessInches
  );
}
